// Decides whether this build is allowed to send ANYTHING to Mixpanel.
//
// Pure module — no Angular, Firebase or Capacitor imports — so the decision can be
// unit-tested on its own with a plain object standing in for browser storage. The
// matching telemetry-mode.test.ts is where the rules are pinned.
//
// A dev build sends nothing at all, because Mixpanel charges per event across every
// project together and the automated test runs were sending several times more events
// than real customers did. Somebody working on telemetry itself can turn it back on for
// a LOCAL build only, with a browser-storage key.
//
// See design-docs/telemetry.md, "Which builds send telemetry at all, and how many
// sessions get recorded", for why the opt-in is shaped the way it is and what it may
// never reach.

export enum TelemetryMode {
  /** This build sends telemetry normally. Production. */
  Enabled = 'enabled',
  /** A local build with the developer opt-in switched on. Sends normally. */
  EnabledByLocalOptIn = 'enabled-by-local-opt-in',
  /** Sends nothing at all: no SDK set-up, no auto-capture, no session replay, no events. */
  Disabled = 'disabled',
}

/**
 * The browser-storage key a developer sets to turn telemetry on for a local build.
 * Set it in the browser's developer tools and reload the page:
 *   localStorage.cs_enable_telemetry = '1'
 */
export const TELEMETRY_LOCAL_OPT_IN_KEY = 'cs_enable_telemetry';

/**
 * The only value that counts as "on". Anything else — including 'true', 'yes' and an
 * empty string — leaves telemetry off. Silence is the direction that costs nothing and
 * surprises nobody, so a half-remembered value must not switch sending on.
 */
export const TELEMETRY_LOCAL_OPT_IN_VALUE = '1';

export interface TelemetryModeInput {
  /**
   * Does this environment send telemetry at all? True for production, false for the
   * dev build our test machines use and for a local build.
   */
  environmentSendsTelemetry: boolean;
  /**
   * Angular's production-mode flag for this build. False ONLY on a local build; both
   * the deployed dev build and the production build set it true.
   */
  isAngularProduction: boolean;
  /**
   * What was read out of browser storage for TELEMETRY_LOCAL_OPT_IN_KEY, or null when
   * the key is absent or could not be read at all.
   */
  localOptInValue: string | null;
}

export function resolveTelemetryMode(input: TelemetryModeInput): TelemetryMode {
  // an environment that sends telemetry always sends it — the opt-in below can only
  // ever turn sending ON, so there is nothing here that could switch production off
  if (input.environmentSendsTelemetry) {
    return TelemetryMode.Enabled;
  }

  // the opt-in is honoured on a local build only, so it can never reach a test machine
  // or a customer's device however it got into that browser's storage
  if (!input.isAngularProduction && input.localOptInValue === TELEMETRY_LOCAL_OPT_IN_VALUE) {
    return TelemetryMode.EnabledByLocalOptIn;
  }

  return TelemetryMode.Disabled;
}

/** True when this mode is allowed to send. The one place that spelling lives. */
export function telemetryModeSends(mode: TelemetryMode): boolean {
  return mode !== TelemetryMode.Disabled;
}

/**
 * Read the opt-in out of browser storage without ever throwing.
 *
 * Reading localStorage really can fail rather than merely come back empty — a browser
 * set to block site data throws on access, and so does a page opened from a file. A
 * throw here would happen inside the telemetry service's constructor, which runs during
 * boot, so it would take the whole app down. An unreadable value is answered as "not
 * set", which leaves telemetry OFF: the cautious direction, because the only thing lost
 * is one developer's opt-in on one machine, and the alternative is sending data from a
 * build that was supposed to be silent.
 */
export function readTelemetryLocalOptIn(storage: Pick<Storage, 'getItem'> | undefined | null): string | null {
  if (!storage) {
    return null;
  }
  try {
    return storage.getItem(TELEMETRY_LOCAL_OPT_IN_KEY);
  } catch {
    // unreadable reads as "not set", which keeps telemetry off — see above
    return null;
  }
}

/**
 * The one line printed at boot saying what telemetry is doing and, when it is off, how
 * to turn it on. Written here rather than at the call site so the test can pin that a
 * disabled build always tells the reader the way back in — a build that silently sends
 * nothing is indistinguishable from a build that is broken.
 */
export function describeTelemetryMode(mode: TelemetryMode): string {
  switch (mode) {
    case TelemetryMode.Enabled:
      return 'Telemetry: enabled for this environment.';
    case TelemetryMode.EnabledByLocalOptIn:
      return `Telemetry: enabled by the local opt-in (localStorage.${TELEMETRY_LOCAL_OPT_IN_KEY}).`;
    case TelemetryMode.Disabled:
      return `Telemetry: DISABLED for this build — nothing is sent to Mixpanel (no events, no auto-capture, no session replay). `
        + `On a local build you can turn it on with: localStorage.${TELEMETRY_LOCAL_OPT_IN_KEY} = '${TELEMETRY_LOCAL_OPT_IN_VALUE}' — then reload.`;
  }
}

// ---------------------------------------------------------------------------
// How many sessions get recorded — the one dial for BOTH replay and heatmap.
// ---------------------------------------------------------------------------

/**
 * The share of sessions to record when the config key is missing or unusable.
 *
 * Twenty-five rather than nought or a hundred, deliberately, and it is the value the
 * config document actually holds in production so an outage changes nothing. Nought
 * would mean a config outage silently switches recording off, and nobody notices a
 * report that has quietly stopped filling up. A hundred would record every session,
 * restoring the event cost this exists to remove — Mixpanel's heatmap capture rides on
 * the same sessions, so it was 70-75% of everything production sent while this stood at
 * 100. See design-docs/telemetry.md for what that does and does not cost.
 */
export const DEFAULT_REPLAY_PERCENT = 25;

/** What resolveReplayPercent worked out, and whether the configured value was usable. */
export interface SamplingPercent {
  /** The share to use, always 0-100. */
  percent: number;
  /**
   * True when a value WAS configured but could not be used. The caller reports this;
   * an absent value is the ordinary case and never sets it.
   */
  malformed: boolean;
}

/**
 * Turn whatever came back from the config database into a usable percentage.
 *
 * The config service hands back the stored JSON with no type normalisation
 * (`ConfigDbService.resolveValue`), so an operator who writes "50" instead of 50 really
 * does produce a string here. Those are converted rather than refused.
 *
 * A value that is present but unusable is NOT treated the same as an absent one. It
 * falls back to the default AND says so, so the caller can report it. Two mistakes are
 * worth spelling out because coercing them quietly is the obvious thing to write and is
 * wrong in both directions: a mistyped 1000 must NOT be clamped to 100, because that
 * records every session and brings back the full cost this sampling exists to remove,
 * silently; and a mistyped -1 must not become 0, which would stop recording entirely
 * with nobody told. Both land on the default instead, and so does a boolean. This
 * mirrors resolveRolloutPercent in shared/src/review-prompt-gate.ts, whose caller
 * reports its malformed flag the same way — but NOT in how it reads a boolean; the
 * branch below says why the two differ.
 */
export function resolveReplayPercent(configured: unknown): SamplingPercent {
  // nothing configured at all -- the ordinary case, and not a problem to report. this is
  // also what a config database that could not be reached looks like, and the two cannot
  // be told apart here.
  if (configured === undefined || configured === null) {
    return { percent: DEFAULT_REPLAY_PERCENT, malformed: false };
  }
  // A boolean is reported rather than converted, and it MUST be caught before the
  // conversion below. Number(false) is 0 and Number(true) is 1, so letting one through
  // would switch recording off for everybody, or narrow it to 1%, in both cases silently
  // and in both cases looking exactly like a share somebody chose. This key is a
  // percentage and a boolean has no percentage meaning, so it is somebody's mistake --
  // a plausible one, since several of its neighbours in ConfigKey ARE plain boolean flags.
  //
  // (resolveRolloutPercent in shared/src/review-prompt-gate.ts, which the rest of this
  // function mirrors, instead reads a boolean as the on/off it plainly means. That is
  // right for a feature switch and wrong here: 'recording on' has no defensible
  // percentage, and guessing 100 would restore the whole cost this exists to remove.)
  if (typeof configured === 'boolean') {
    return { percent: DEFAULT_REPLAY_PERCENT, malformed: true };
  }

  // a blank value is handled before any conversion too: Number('') and Number('   ') are
  // both 0, so letting one through would also silently switch recording off for everybody
  if (String(configured).trim() === '') {
    return { percent: DEFAULT_REPLAY_PERCENT, malformed: false };
  }

  const asNumber = typeof configured === 'number' ? configured : Number(configured);
  if (!Number.isFinite(asNumber)) {
    return { percent: DEFAULT_REPLAY_PERCENT, malformed: true };
  }
  if (asNumber < 0 || asNumber > 100) {
    return { percent: DEFAULT_REPLAY_PERCENT, malformed: true };
  }
  return { percent: asNumber, malformed: false };
}
